import { reportAttachmentMap, bureauAttachmentTypes, contractorAttachmentTypes } from '@/support/ReportAttachments';
import { Component, Vue, Model, Prop, Emit } from 'vue-property-decorator';
import { Report as ReportModel, Media } from '@/models/Report';
import UploadFileDialog from '@/components/UploadFileDialog/UploadFileDialog.vue';
import { MiDialogOptions } from '@/components/MiDialog/MiDialog';
import AttachmentTable from '@/components/AttachmentTable/AttachmentTable.vue';
import { AxiosError } from 'axios';
import ErrorHandler from '@/support/ErrorHandler';
import { clone } from 'lodash';
import { Library as LibraryModel } from '@/models/Library';

@Component<Attachments>({
  components: {
    UploadFileDialog,
    AttachmentTable,
  },
})
export default class Attachments extends Vue {
  public $pageTitle = 'Attachments';

  @Model('report', { default: () => new ReportModel() })
  protected report!: ReportModel;

  @Prop({ default: true })
  protected allowEdit!: boolean;

  @Prop({ default: false })
  protected syncing!: boolean;

  protected isCreatingAttachment = false;

  protected isEvaluation = false;

  protected isLoading = false;

  protected selectedAttachment: Media | null = null;

  protected optionalAttachments: Media[] = [];

  protected bureauAttachments: Media[] = [];

  protected contractorAttachments: Media[] = [];

  protected totalBureauFiles = 0;

  protected totalBureauFilesUploaded = 0;

  protected totalContractorFiles = 0;

  protected totalContractorFilesUploaded = 0;

  @Emit()
  protected acceptContract(): string {
    return '';
  }

  protected mounted(): void {
    this.initialize();
  }

  protected async initialize(): Promise<void> {
    this.isLoading = true;
    await this.getAttachments();
    this.isLoading = false;
  }

  protected async handleFileUploaded(): Promise<void> {
    this.close();
    this.isLoading = true;
    await this.getAttachments();
    this.isLoading = false;
  }

  protected async handleEvaluationUploaded(): Promise<void> {
    this.close();
    this.$emit('reload');
  }

  protected close(): void {
    this.isEvaluation = false;
    this.isCreatingAttachment = false;
    this.selectedAttachment = null;
  }

  protected isScopedSlot(name: string): boolean {
    return Object.keys(this.$scopedSlots).some((key: string) => key.includes(name));
  }

  public async viewAttachment(id: string): Promise<void> {
    const file = await new LibraryModel().getFile(id);
    window.open(file);
  }

  protected editAttachment(attachment: Media): void {
    this.selectedAttachment = clone(attachment);
    this.isEvaluation = this.selectedAttachment.type === 'evaluation' || false;
    this.isCreatingAttachment = true;
  }

  protected deleteAttachment(attachment: Media): void {
    this.selectedAttachment = attachment;
    this.$store.dispatch('openDialog', this.dialogOptionsDelete);
  }

  protected addEvaluation(): void {
    this.selectedAttachment = {
      type: 'evaluation',
      file_name: 'Prestatieverklaring',
      description: 'Prestatieverklaring',
      hin_show_to_all: null,
      sort_order: 0,
      original: '',
    };
    this.isEvaluation = true;
    this.isCreatingAttachment = true;
  }

  protected async getAttachments(): Promise<void> {
    if (! this.report) { return; }

    const response = await this.report.listMedia({
      type: 'attachments',
    })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });

    // Add dummy files for required files that are not uploaded yet.
    if (response) {
      this.bureauAttachments = [...this.getRequiredAttachments(response, 'bureau')];
      this.contractorAttachments = [...this.getRequiredAttachments(response, 'contractor')];
      this.optionalAttachments = [...this.getOptionalAttachments(response)];
    }

    this.isLoading = false;
  }

  protected handleDeleteAttachment(): void {
    if (! this.report || ! this.selectedAttachment) {
      return;
    }
    this.isLoading = true;
    this.report
      .deleteMediaEndpoint(this.selectedAttachment.uuid ? this.selectedAttachment.uuid : '')
      .then(() => {
        this.initialize();
        this.close();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected getRequiredAttachments(attachments: Media[], role: 'bureau' | 'contractor'): Media[] {
    let requiredAttachmentTypes: string[] = [];

    if (role === 'bureau') {
      requiredAttachmentTypes = bureauAttachmentTypes;
    }

    if (role === 'contractor') {
      requiredAttachmentTypes = contractorAttachmentTypes;
    }

    const uploadedRequiredFiles = attachments.filter((media: Media): boolean => !! media.type && requiredAttachmentTypes.includes(media.type));

    const requiredFiles: Media[] = [...uploadedRequiredFiles];

    let count = 0;
    let uploadedCount = 0;

    requiredAttachmentTypes.forEach((type: string) => {
      count += 1;
      const isUploaded = uploadedRequiredFiles.filter((media: Media): boolean => !! media.type && media.type === type);

      if (! isUploaded.length) {
        const status = reportAttachmentMap.find((attachment: Media) => attachment.type === type);
        if (! status) {
          return;
        }
        requiredFiles.push(status);
      } else {
        uploadedCount += 1;
      }
    });

    if (role === 'bureau') {
      this.totalBureauFiles = count;
      this.totalBureauFilesUploaded = uploadedCount;
    }

    if (role === 'contractor') {
      this.totalContractorFiles = count;
      this.totalContractorFilesUploaded = uploadedCount;
    }

    return requiredFiles;
  }

  protected getOptionalAttachments(attachments: Media[]): Media[] {
    return attachments.filter((media) => ! media.type || (! bureauAttachmentTypes.includes(media.type) && ! contractorAttachmentTypes.includes(media.type)));
  }

  protected get reportHasNonEditableStatus(): boolean {
    return ['hin_waiting_for_signature', 'hin_assignment_accepted', 'hin_assignment_started', 'hin_assignment_completed', 'hin_evaluation_uploaded', 'hin_closed'].includes(this.report?.status_raw || '');
  }

  protected get isAttachmentEditable(): boolean {
    return ! ['hin_closed', 'hin_aborted'].includes(this.report?.status_raw || '');
  }

  protected get canAccept(): boolean {
    return this.$store.getters.isContractor && this.report.status_raw === 'hin_waiting_for_signature';
  }

  protected get dialogOptionsDelete(): MiDialogOptions {
    return {
      title: 'Bevestigen',
      text: 'Weet je zeker dat je deze bijlage wilt verwijderen?',
      type: 'warning',
      buttons: {
        confirm: {
          color: 'warning',
          text: 'Verwijderen',
          action: () => {
            this.handleDeleteAttachment();
          },
        },
        cancel: {
          text: 'Annuleren',
          color: 'text-light',
          action: () => {
            this.selectedAttachment = null;
          },
        },
      },
    };
  }
}
